// frontend/src/pages/user-dashboard/index.jsx
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import api, {
  fetchAdvicePasswordsMe,
  fetchPortsMe,
  fetchMe,
  createAdvicePasswordMe,
  updateAdvicePasswordMe,
  deleteAdvicePasswordMe,
  getMyGatewayPorts,
  updateMyGatewayPort,
  fetchMyUniqueSites,
  checkGatewayAccess,
  syncPortsFromHardware,
  fetchMessagesIndexedByPort,
  getSessionStampWho,
  getSessionStampedAs,
  requestSimConversion,
  requestSimConversionBulk,
  fetchMyTmAccountRequests,
} from "../../api";
import {
  PortActivationBadge,
  getActivationButtonState,
  canRequestSimConversion,
  adviceRowMatchesConversionFilter,
  matchesConversionStatusFilter,
  indexPortsByAssignedDigits,
} from "../../shared/portActivation";
import {
  tmRequestRowKey,
  pendingTmRequestKeySet,
  pendingTmRequestsByKey,
  completedTmByOriginalKey,
  completedTmByNewKey,
  tmAccountRoleSearchKind,
  adviceRowMatchesTmRoleSearch,
  expandAdviceRowsWithTmPairs,
} from "../../shared/tmAccountRequest";
import { liveSmsUpdate, mergePortMessageMaps } from "../../shared/smsMessages";
import InlineNotice from "../../components/InlineNotice";
import SearchableSelect from "./components/SearchableSelect";

import AdvicePasswordsTable from "./components/AdvicePasswordsTable";
import UnmatchedPortsTable from "./components/UnmatchedPortsTable";
import DetailsModal from "./components/DetailsModal";
import ReportProblemModal from "./components/ReportProblemModal";
import ForwardingApprovalModal from "./components/ForwardingApprovalModal";
import UserPortStatusPanel from "./components/UserPortStatusPanel";
import ConvertToTtOtpModal from "./components/ConvertToTtOtpModal";
import ConversionStatusFilter from "./components/ConversionStatusFilter";
// MyGateway moved to separate tab - no longer imported here

import usePortsByEmail, { inferMissingPortEmails } from "./hooks/usePortsByEmail";
import useDuplicateDetection from "./hooks/useDuplicateDetection";
import usePortActivation from "./hooks/usePortActivation";
import useSmartActivationPolling from "./hooks/useSmartActivationPolling";
import useSiteRequests from "../site-requests/useSiteRequests";

/** Renders nothing; runs smart polling for one port and cleans up on unmount. */
function PollingRunner({
  portId,
  gatewayId,
  portNumber,
  setPortStatusFromActivity,
  stopActivationPolling,
  refreshActivation,
}) {
  const polling = useSmartActivationPolling({
    portId,
    gatewayId,
    portNumber,
    onStatusChange: (data) => {
      setPortStatusFromActivity({
        [portId]: {
          status: data.status,
          signal: data.signal,
          operator: data.operator,
          timestamp: Date.now(),
        },
      });
    },
    onComplete: () => {
      stopActivationPolling(portId);
      refreshActivation();
    },
  });

  useEffect(() => {
    polling.start();
    return () => polling.stop();
  }, []);

  return null;
}

function groupAdviceByUserCode(adviceList, currentUserCode) {
  if (!Array.isArray(adviceList) || adviceList.length === 0) {
    return [];
  }
  const groups = {};
  adviceList.forEach((row) => {
    const userCode = row.user_code || "UNKNOWN";
    if (!groups[userCode]) {
      groups[userCode] = [];
    }
    groups[userCode].push(row);
  });
  const groupArray = Object.entries(groups).map(([userCode, rows]) => ({
    userCode,
    rows,
  }));
  groupArray.sort((a, b) => {
    if (a.userCode === currentUserCode) return -1;
    if (b.userCode === currentUserCode) return 1;
    return a.userCode.localeCompare(b.userCode);
  });
  return groupArray;
}

export default function UserDashboard() {
  // Route safety - check authentication and redirect if needed
  useEffect(() => {
    const token = localStorage.getItem("token");
    const role = localStorage.getItem("role");
    
    if (!token) {
      window.location.href = "/userlogin";
      return;
    }
    if (role && role !== "user") {
      window.location.href = "/protected";
      return;
    }
  }, []);

  // Data state
  const [advice, setAdvice] = useState([]);
  const [ports, setPorts] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // Activity state
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityError, setActivityError] = useState("");
  const [activityBlocked, setActivityBlocked] = useState(false);

  // Hardware sync state
  const [syncing, setSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);

  // Global notice
  const [notice, setNotice] = useState(null);

  // Sorting + search
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [query, setQuery] = useState("");
  const [columnFilters, setColumnFilters] = useState({
    userCode: "",
    site: "",
    email: "",
    password: "",
    numbers: "",
    personaCheck: "",
    selfTransfer: "",
    conversionStatus: "",
  });
  const [completedTmOriginal, setCompletedTmOriginal] = useState(() => new Map());
  const [completedTmNew, setCompletedTmNew] = useState(() => new Map());
  const [pendingTmKeys, setPendingTmKeys] = useState(() => new Set());
  const [tmRequestsByKey, setTmRequestsByKey] = useState(() => new Map());

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      fetchMyTmAccountRequests({ status: "pending" }).catch(() => []),
      fetchMyTmAccountRequests({ status: "completed" }).catch(() => []),
    ])
      .then(([pending, completed]) => {
        if (cancelled) return;
        setPendingTmKeys(pendingTmRequestKeySet(pending));
        setTmRequestsByKey(pendingTmRequestsByKey(pending));
        setCompletedTmOriginal(completedTmByOriginalKey(completed));
        setCompletedTmNew(completedTmByNewKey(completed));
      })
      .catch(() => {
        if (cancelled) return;
        setPendingTmKeys(new Set());
        setTmRequestsByKey(new Map());
        setCompletedTmOriginal(new Map());
        setCompletedTmNew(new Map());
      });
    return () => {
      cancelled = true;
    };
  }, [advice]);

  // Row editing
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({ site: "", email: "", password: "" });

  // Details modal
  const [showDetails, setShowDetails] = useState(false);
  const [detailsRecord, setDetailsRecord] = useState(null);

  // Report Problem modal
  const [showReportProblem, setShowReportProblem] = useState(false);
  const [reportProblemPort, setReportProblemPort] = useState(null);
  const [showForwardingApproval, setShowForwardingApproval] = useState(false);
  const [forwardingPrefillEmail, setForwardingPrefillEmail] = useState("");
  const [bulkConversionOpen, setBulkConversionOpen] = useState(false);

  // Adding new row
  const [adding, setAdding] = useState(false);
  const [newRow, setNewRow] = useState({ site: "", email: "", password: "" });

  // Sites list for dropdown
  const [sites, setSites] = useState([]);

  // Gateway ID for port activity filtering
  const [gatewayId, setGatewayId] = useState(null);

  // Messages state: map of portId -> array of messages
  const [portMessages, setPortMessages] = useState({});

  const userCode = localStorage.getItem("user_code") || "";
  const displayPorts = useMemo(
    () => inferMissingPortEmails(ports, advice),
    [ports, advice]
  );
  const portsByEmail = usePortsByEmail(displayPorts);
  const portsByDigits = useMemo(
    () => indexPortsByAssignedDigits(ports),
    [ports]
  );
  const eligibleConversionPorts = useMemo(
    () => (ports || []).filter((p) => canRequestSimConversion(p)),
    [ports]
  );

  const mergeAssignedPorts = useCallback((mine) => {
    const list = Array.isArray(mine) ? mine : [];
    setPorts((prev) => {
      const byId = new Map();
      for (const p of prev || []) {
        if (p?.id != null) byId.set(p.id, p);
      }
      for (const p of list) {
        if (p?.id != null && !byId.has(p.id)) byId.set(p.id, p);
      }
      return [...byId.values()];
    });
  }, []);

  const openBulkConversion = useCallback(async () => {
    try {
      const mine = await fetchPortsMe({ skipCache: true });
      mergeAssignedPorts(mine);
    } catch (e) {
      console.error("Failed to load assigned ports for conversion", e);
    }
    setBulkConversionOpen(true);
  }, [mergeAssignedPorts]);
  const { duplicateEmails, duplicateNumbers } = useDuplicateDetection(advice, portsByEmail);

  /** Hook for port activation */
  const {
    portStatus,
    portMessages: activationPortMessages,
    portCountdowns,
    clearPortMessage,
    onActivatePort,
    setPortStatusFromActivity,
    startCountdown,
    pollingPorts,
    stopActivationPolling,
    refreshActivation,
    activationLoading,
    activationError,
  } = usePortActivation(ports, setPorts, { scope: "me", adaptivePoll: true });

  const { submitRequest: submitSiteRequest } = useSiteRequests({ mine: true });

  const showToast = useCallback(
    (message, kind = "info") => setNotice({ text: message, kind }),
    []
  );

  const signOut = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("role");
    localStorage.removeItem("user_code");
    sessionStorage.removeItem("vault_signup_authenticated");
    window.location.href = "/userlogin";
  };

  useEffect(() => {
    if (activationError?.includes?.("Forbidden") || activationError?.includes?.("403")) {
      setActivityBlocked(true);
      setActivityError(activationError);
    } else if (activationError) {
      setActivityError(activationError);
    }
  }, [activationError]);

  useEffect(() => {
    setActivityLoading(activationLoading);
  }, [activationLoading]);

  /** Sync hardware → PortActivation rows then refresh displayed status */
  const handleSyncFromHardware = useCallback(async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      await syncPortsFromHardware();
      setLastSyncTime(new Date());
      await refreshActivation();
    } catch {
      // sync errors are non-fatal
    } finally {
      setSyncing(false);
    }
  }, [syncing, refreshActivation]);

  const syncFromHardwareRef = useRef(handleSyncFromHardware);
  useEffect(() => { syncFromHardwareRef.current = handleSyncFromHardware; }, [handleSyncFromHardware]);

  /** Initial load — run once on mount */
  useEffect(() => {
    let cancelled = false;
    let allPorts = [];
    (async () => {
      try {
        const [a, pr, gatewayPorts, me, sitesList] = await Promise.all([
          fetchAdvicePasswordsMe().catch(() => []),
          fetchPortsMe().catch(() => []),
          getMyGatewayPorts().catch(() => []), // Fetch gateway ports
          fetchMe().catch(() => null),
          fetchMyUniqueSites().catch(() => []),
        ]);
        // Build port list before cancelled check so finally always has allPorts (avoids StrictMode first run leaving it [])
        const regularPorts = pr || [];
        const gatewayPortsList = gatewayPorts || [];
        const portMap = new Map();
        regularPorts.forEach(port => {
          if (port?.id) portMap.set(port.id, port);
        });
        gatewayPortsList.forEach(port => {
          if (!port?.id) return;
          const existing = portMap.get(port.id);
          const hasNewEmail = (port.user_email || '').trim().length > 0;
          const hasExistingEmail = (existing?.user_email || '').trim().length > 0;
          if (!existing || (hasNewEmail && !hasExistingEmail)) {
            portMap.set(port.id, port);
          }
        });
        allPorts = Array.from(portMap.values());

        if (!cancelled) {
          setAdvice(a || []);
          setPorts(allPorts);
          setProfile(me || null);
          setSites(sitesList || []);
          setLoading(false);
        }
      } catch (e) {
        if (!cancelled) {
          setErr(e?.response?.data?.detail || e?.message || "Failed to load your data");
          setLoading(false);
        }
      } finally {
        syncFromHardwareRef.current().catch(() => {});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const messagePortIdsKey = useMemo(
    () =>
      (ports || [])
        .map((p) => p?.id)
        .filter((id) => id != null)
        .map(Number)
        .filter((id) => Number.isFinite(id))
        .sort((a, b) => a - b)
        .join(","),
    [ports]
  );

  // Existing texts once, when this buyer's ports are known. Later texts arrive on the socket.
  useEffect(() => {
    const ids = messagePortIdsKey
      ? messagePortIdsKey.split(",").map((id) => Number(id)).filter((id) => Number.isFinite(id))
      : [];
    if (!ids.length) return undefined;
    let cancelled = false;

    (async () => {
      const messageMap = await fetchMessagesIndexedByPort(ids, 10);
      if (!cancelled) {
        setPortMessages((prev) => mergePortMessageMaps(prev, messageMap));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [messagePortIdsKey]);

  /** Handover countdown warning */
  useEffect(() => {
    Object.entries(portStatus).forEach(([id, s]) => {
      const pid = Number(id);
      // Check both number and string keys for countdown (handle type inconsistency)
      const hasCountdown = portCountdowns[pid] != null || portCountdowns[id] != null;
      if (s?.status === "active" && s?.show_30s_warning && !hasCountdown) {
        const secs = Math.max(5, s?.remaining_seconds ?? 30);
        startCountdown(pid, secs);
      }
    });
  }, [portStatus, portCountdowns, startCountdown]);

  // Handle countdown finish → refresh if still flagged
  const prevCountdownsRef = useRef(portCountdowns);
  useEffect(() => {
    const prev = prevCountdownsRef.current;
    const finishedIds = [];
    Object.keys(prev).forEach((pid) => {
      if (!(pid in portCountdowns)) finishedIds.push(Number(pid));
    });
    if (finishedIds.length > 0) {
      const needsRefresh = finishedIds.some((pid) => {
        const s = portStatus[pid];
        return s?.status === "active" && s?.show_30s_warning === true;
      });
      if (needsRefresh) setTimeout(() => refreshActivation(), 300);
    }
    prevCountdownsRef.current = portCountdowns;
  }, [portCountdowns, portStatus, refreshActivation]);

  const portsRef = useRef(ports);
  const activityBlockedRef = useRef(activityBlocked);
  const setPortStatusFromActivityRef = useRef(setPortStatusFromActivity);
  const startCountdownRef = useRef(startCountdown);
  const refreshActivationRef = useRef(refreshActivation);
  portsRef.current = ports;
  activityBlockedRef.current = activityBlocked;
  setPortStatusFromActivityRef.current = setPortStatusFromActivity;
  startCountdownRef.current = startCountdown;
  refreshActivationRef.current = refreshActivation;

  /** Stays subscribed for the life of the page. A new SMS is written into the row as it arrives. */
  useEffect(() => {
    let statusTimer = null;
    const unsubscribe = api.subscribeTo("new_message", (messageData) => {
      const update = liveSmsUpdate(portsRef.current, messageData);
      if (!update) return;
      const { portId, message } = update;

      if (!activityBlockedRef.current && !message.is_outgoing) {
        setPortStatusFromActivityRef.current({
          [portId]: {
            status: "active",
            timestamp: Date.now(),
            remaining_seconds: 60,
            message_triggered: true,
          },
        });
        startCountdownRef.current(portId, 60);
        if (statusTimer) clearTimeout(statusTimer);
        statusTimer = setTimeout(() => refreshActivationRef.current(), 5500);
      }

      setPortMessages((prev) => mergePortMessageMaps(prev, { [portId]: [message] }));
    });

    return () => {
      if (statusTimer) clearTimeout(statusTimer);
      if (typeof unsubscribe === "function") unsubscribe();
    };
  }, []);

  const digits = (s) => String(s || "").replace(/\D/g, "");

  const handleActivatePort = useCallback(
    async (phoneNumberOrPort) => {
      let phoneNumber;
      let portId;

      if (typeof phoneNumberOrPort === "string") {
        phoneNumber = phoneNumberOrPort;
        const match = ports.find(
          (p) => digits(p.assigned_number) === digits(phoneNumber)
        );
        if (!match || !match.port_number || !match.id) {
          showToast(`No port mapped to ${phoneNumber}`);
          return;
        }
        portId = match.id;
      } else if (
        typeof phoneNumberOrPort === "object" &&
        phoneNumberOrPort !== null
      ) {
        phoneNumber = phoneNumberOrPort.assigned_number;
        portId = phoneNumberOrPort.id;

        if (!phoneNumber || !portId) {
          showToast(`Invalid port data`);
          return;
        }
      } else {
        showToast(`Invalid activation parameter`);
        return;
      }

      await onActivatePort(portId, phoneNumber);
      showToast("Checking activation status…", "info");
      // After activation: sync hardware state then load activity so the
      // dashboard immediately reflects what the hardware reports.
      setTimeout(async () => {
        await syncFromHardwareRef.current().catch(() => {});
        refreshActivation();
      }, 2000);
    },
    [ports, showToast, onActivatePort, refreshActivation]
  );

  const handleReportProblem = useCallback((port) => {
    setReportProblemPort(port);
    setShowReportProblem(true);
  }, []);

  const handleRequestSimConversion = useCallback(async (port) => {
    const updated = await requestSimConversion(port.id);
    setPorts((prev) => prev.map((p) => (p.id === port.id ? { ...p, ...updated } : p)));
    showToast(
      updated?.req_sim_conversion_id
        ? `TT OTP conversion requested (request #${updated.req_sim_conversion_id})`
        : "TT OTP conversion requested",
      "success"
    );
    return updated;
  }, [showToast]);

  const handleBulkRequestSimConversion = useCallback(async (selectedPorts) => {
    const ids = (selectedPorts || []).map((p) => p.id).filter((id) => id != null);
    if (!ids.length) {
      throw new Error("Select at least one number");
    }
    const result = await requestSimConversionBulk(ids);
    const updatedById = new Map(
      (result?.requested || []).map((p) => [p.id, p])
    );
    if (updatedById.size) {
      setPorts((prev) =>
        prev.map((p) => (updatedById.has(p.id) ? { ...p, ...updatedById.get(p.id) } : p))
      );
    }
    const n = result?.requested?.length || 0;
    const skipped = result?.skipped?.length || 0;
    if (!n && skipped) {
      throw new Error(
        result.skipped[0]?.reason || "None of the selected numbers could be requested"
      );
    }
    showToast(
      skipped
        ? `TT OTP conversion requested for ${n} number(s); ${skipped} skipped${
            result?.request_id ? ` (request #${result.request_id})` : ""
          }`
        : `TT OTP conversion requested for ${n} number(s)${
            result?.request_id ? ` (request #${result.request_id})` : ""
          }`,
      "success"
    );
    return result;
  }, [showToast]);

  const handleRowForwardingApproval = useCallback((email) => {
    setForwardingPrefillEmail(email || "");
    setShowForwardingApproval(true);
  }, []);

  // Handler to update port phone number (for gateway ports)
  const handleUpdatePort = useCallback(async (portId, updates) => {
    try {
      // Check if this is a gateway port by finding it in the ports list
      const port = ports.find(p => p.id === portId);
      if (port) {
        // Gateway ports and service numbers assigned to this user
        const updated = await updateMyGatewayPort(portId, updates);
        // Update local state
        setPorts(prev => prev.map(p => p.id === portId ? updated : p));
        setNotice({ text: "Phone number updated successfully", kind: "success" });
      } else {
        throw new Error("Port not found");
      }
    } catch (err) {
      console.error('Error updating port:', err);
      setNotice({ 
        text: err.response?.data?.detail || err.message || "Failed to update phone number", 
        kind: "error" 
      });
      throw err;
    }
  }, [ports]);

  const handleAssignPort = useCallback(async (portId, email) => {
    try {
      const port = ports.find((p) => p.id === portId);
      if (!port) {
        throw new Error("Port not found");
      }

      const existingEmail = (port.user_email || "").trim().toLowerCase();
      const targetEmail = email.trim().toLowerCase();
      const alreadyOwned = existingEmail && existingEmail !== targetEmail;

      if (alreadyOwned) {
        // Port belongs to another email — add the number as a user_managed_number
        // on the target advice record instead of stealing the port
        const adviceList = await fetchAdvicePasswordsMe().catch(() => []);
        const targetAdvice = adviceList.find(
          (a) => (a.email || "").trim().toLowerCase() === targetEmail
        );
        if (!targetAdvice) {
          throw new Error(`No advice record found for ${email}`);
        }
        const existing = targetAdvice.user_managed_numbers || [];
        const num = port.assigned_number || "";
        if (num && !existing.includes(num)) {
          await updateAdvicePasswordMe(targetAdvice.id, {
            user_managed_numbers: [...existing, num],
          });
        }
        setNotice({ text: `${num} added to ${email} as a shared number`, kind: "success" });
      } else {
        // Port is unowned — assign it directly
        await updateMyGatewayPort(portId, { user_email: email });
        setNotice({ text: "Port assigned successfully", kind: "success" });
      }

      // Refresh both ports and advice
      const [pr, gatewayPorts, updatedAdvice] = await Promise.all([
        fetchPortsMe().catch(() => []),
        getMyGatewayPorts().catch(() => []),
        fetchAdvicePasswordsMe().catch(() => advice),
      ]);
      const portMap = new Map();
      (pr || []).forEach((p) => { if (p?.id) portMap.set(p.id, p); });
      (gatewayPorts || []).forEach((p) => {
        if (!p?.id) return;
        const existing = portMap.get(p.id);
        const hasNewEmail = (p.user_email || '').trim().length > 0;
        const hasExistingEmail = (existing?.user_email || '').trim().length > 0;
        if (!existing || (hasNewEmail && !hasExistingEmail)) portMap.set(p.id, p);
      });
      setPorts(Array.from(portMap.values()));
      setAdvice(updatedAdvice || advice);
    } catch (err) {
      setNotice({ text: err?.response?.data?.detail || err.message || "Failed to assign port", kind: "error" });
      throw err;
    }
  }, [ports, advice]);

  const handleClearPort = useCallback(
    async (portId) => {
      try {
        await updateMyGatewayPort(portId, { assigned_number: "", user_email: "" });
        setNotice({ text: "Port cleared – slot free for next SIM", kind: "success" });
        const [pr, gatewayPorts] = await Promise.all([
          fetchPortsMe().catch(() => []),
          getMyGatewayPorts().catch(() => []),
        ]);
        const regularPorts = pr || [];
        const gatewayPortsList = gatewayPorts || [];
        const portMap = new Map();
        regularPorts.forEach((p) => {
          if (p?.id) portMap.set(p.id, p);
        });
        gatewayPortsList.forEach((p) => {
          if (p?.id && !portMap.has(p.id)) portMap.set(p.id, p);
        });
        setPorts(Array.from(portMap.values()));
      } catch (err) {
        console.error("Error clearing port:", err);
        setNotice({
          text: err?.response?.data?.detail || err?.message || "Failed to clear port",
          kind: "error",
        });
      }
    },
    [updateMyGatewayPort, fetchPortsMe, getMyGatewayPorts]
  );

  const handleDeleteAdvicePassword = useCallback(
    async (id) => {
      try {
        await deleteAdvicePasswordMe(id);
        setNotice({ text: "Email removed from dashboard", kind: "success" });
        const updated = await fetchAdvicePasswordsMe().catch(() => advice);
        setAdvice(updated || advice);
      } catch (err) {
        console.error("Error deleting advice password:", err);
        setNotice({
          text: err?.response?.data?.detail || err?.message || "Failed to remove email",
          kind: "error",
        });
      }
    },
    [deleteAdvicePasswordMe, fetchAdvicePasswordsMe, advice]
  );

  const mergeAdviceRowsMatching = useCallback((sourceRow, patch) => {
    const email = (sourceRow?.email || "").trim().toLowerCase();
    const uc = sourceRow?.user_code || "";
    setAdvice((prev) =>
      (prev || []).map((row) =>
        (row.user_code || "") === uc &&
        (row.email || "").trim().toLowerCase() === email
          ? { ...row, ...patch }
          : row
      )
    );
  }, []);

  const handlePersonaCheckSubmit = useCallback(
    async (row, { answers }) => {
      const stampedAs = getSessionStampedAs();
      const stampedBy = getSessionStampWho();

      const updated = await updateAdvicePasswordMe(row.id, {
        persona_check_triggered: true,
        persona_check_stamped_as: stampedAs,
        persona_check_stamped_by: stampedBy,
        persona_check_answers: answers,
      });
      mergeAdviceRowsMatching(row, {
        persona_check_triggered: true,
        persona_check_triggered_on:
          updated?.persona_check_triggered_on || new Date().toISOString(),
        persona_check_stamped_as: stampedAs,
        persona_check_stamped_by: stampedBy,
        persona_check_answers: answers,
      });
      setNotice({ text: "Persona check saved", kind: "success" });
    },
    [mergeAdviceRowsMatching]
  );

  const handlePersonaCheckClear = useCallback(
    async (row) => {
      await updateAdvicePasswordMe(row.id, { persona_check_triggered: false });
      mergeAdviceRowsMatching(row, {
        persona_check_triggered: false,
        persona_check_triggered_on: null,
        persona_check_stamped_as: null,
        persona_check_stamped_by: null,
        persona_check_answers: null,
      });
      setNotice({ text: "Persona check cleared", kind: "success" });
    },
    [mergeAdviceRowsMatching]
  );

  const handleSelfTransferToggle = useCallback(
    async (row, checked) => {
      const updated = await updateAdvicePasswordMe(row.id, {
        self_manage_transfer: checked,
      });
      mergeAdviceRowsMatching(row, {
        self_manage_transfer: checked,
        self_manage_transfer_since: checked
          ? updated?.self_manage_transfer_since || new Date().toISOString()
          : null,
      });
      setNotice({
        text: checked ? "Self transfer enabled" : "Self transfer disabled",
        kind: "success",
      });
    },
    [mergeAdviceRowsMatching]
  );

  const adviceEmailSet = useMemo(() => {
    const s = new Set();
    (advice || []).forEach((r) => {
      const k = (r.email || "").trim().toLowerCase();
      if (k) s.add(k);
    });
    return s;
  }, [advice]);

  /** --------- FILTERING (more sensitive) --------- */
  const filteredAdvice = useMemo(() => {
    const qRaw = (query || "").trim().toLowerCase();
    const qDigits = (query || "").replace(/\D/g, "");
    const roleKind = tmAccountRoleSearchKind(query);
    const emailRoleKind = tmAccountRoleSearchKind(columnFilters.email);

    let rows = (advice || []).filter((r) => {
      const email = (r.email || "").toLowerCase();
      const site = (r.site || "").toLowerCase();
      const passwd = (r.password || "").toLowerCase();

      // All linked numbers for this email, digits only, space-joined
      const linked = (portsByEmail.get(email) || [])
        .map((p) => (p?.assigned_number ? String(p.assigned_number).replace(/\D/g, "") : ""))
        .join(" ");

      // More sensitive global match:
      // - email/site/password by ANY length (>=1 non-space)
      // - numbers once the user has typed at least 3 digits
      const matchesGlobal =
        !qRaw ||
        email.includes(qRaw) ||
        site.includes(qRaw) ||
        passwd.includes(qRaw) ||
        (qDigits.length >= 3 && linked.includes(qDigits)) ||
        adviceRowMatchesTmRoleSearch(r, roleKind, completedTmOriginal, completedTmNew);

      // Column filters (site/email/password are plain text; numbers by digits)
      const siteFilter = (columnFilters.site || "").toLowerCase();
      const emailFilter = (columnFilters.email || "").toLowerCase();
      const passwordFilter = (columnFilters.password || "").toLowerCase();
      const numbersFilterDigits = (columnFilters.numbers || "").replace(/\D/g, "");
      const personaCheckFilter = columnFilters.personaCheck || "";
      const selfTransferFilter = columnFilters.selfTransfer || "";

      const matchesSite = !siteFilter || site.includes(siteFilter);
      const matchesEmail =
        !emailFilter ||
        email.includes(emailFilter) ||
        adviceRowMatchesTmRoleSearch(r, emailRoleKind, completedTmOriginal, completedTmNew);
      const matchesPassword = !passwordFilter || passwd.includes(passwordFilter);
      const matchesNumbers =
        !numbersFilterDigits || linked.includes(numbersFilterDigits);
      const matchesPersonaCheck =
        !personaCheckFilter ||
        (personaCheckFilter === "yes" && !!r.persona_check_triggered) ||
        (personaCheckFilter === "no" && !r.persona_check_triggered);
      const matchesSelfTransfer =
        !selfTransferFilter ||
        (selfTransferFilter === "yes" && !!r.self_manage_transfer) ||
        (selfTransferFilter === "no" && !r.self_manage_transfer);
      const conversionStatusFilter = columnFilters.conversionStatus || "";
      const matchesConversion =
        conversionStatusFilter === "new_tt_email"
          ? pendingTmKeys.has(tmRequestRowKey(r))
          : adviceRowMatchesConversionFilter(
              r,
              portsByEmail,
              portsByDigits,
              conversionStatusFilter
            );

      return (
        matchesGlobal &&
        matchesSite &&
        matchesEmail &&
        matchesPassword &&
        matchesNumbers &&
        matchesPersonaCheck &&
        matchesSelfTransfer &&
        matchesConversion
      );
    });

    // Keep order-restricted + replacement pairs together in search results.
    rows = expandAdviceRowsWithTmPairs(
      rows,
      advice,
      completedTmOriginal,
      completedTmNew
    );

    if (sortField) {
      rows = [...rows].sort((a, b) => {
        let av = a[sortField];
        let bv = b[sortField];
        if (sortField === "main") {
          av = a.main ? 1 : 0;
          bv = b.main ? 1 : 0;
        }
        av = (av ?? "").toString().toLowerCase();
        bv = (bv ?? "").toString().toLowerCase();
        const cmp = av.localeCompare(bv, undefined, { numeric: true });
        return sortDir === "asc" ? cmp : -cmp;
      });
    }
    return rows;
  }, [advice, portsByEmail, portsByDigits, query, sortField, sortDir, columnFilters, completedTmOriginal, completedTmNew, pendingTmKeys]);

  // Unmatched ports (ports with no email OR email not in advice passwords)
  const unmatchedPorts = useMemo(() => {
    const list = (displayPorts || []).filter((p) => {
      const k = (p.user_email || "").trim().toLowerCase();
      const num = (p.assigned_number || "").replace(/\D/g, "");
      const q = (query || "").toLowerCase();
      const matchesQuery =
        !q ||
        (p.user_email || "").toLowerCase().includes(q) ||
        num.includes(q.replace(/\D/g, ""));
      const conversionStatusFilter = columnFilters.conversionStatus || "";
      const matchesConversion = matchesConversionStatusFilter(
        p,
        conversionStatusFilter
      );
      
      // Port is unmatched if:
      // 1. No email assigned, OR
      // 2. Email exists but is NOT in advice passwords
      // Also check if port is actually linked via portsByEmail (more reliable)
      const isLinked = k && portsByEmail.has(k) && portsByEmail.get(k).some(port => port.id === p.id);
      const isInAdvice = k && adviceEmailSet.has(k);
      
      // Only show if: matches query AND (no email OR email not in advice)
      // AND not actually linked to an advice password row
      return (
        matchesQuery &&
        matchesConversion &&
        (!k || (!isInAdvice && !isLinked))
      );
    });
    list.sort((a, b) => {
      const av = (a.port_number || "").toString();
      const bv = (b.port_number || "").toString();
      return av.localeCompare(bv, undefined, { numeric: true });
    });
    return list;
  }, [displayPorts, adviceEmailSet, query, portsByEmail, columnFilters.conversionStatus]);

  const startEdit = (r) => {
    setEditingId(r.id);
    setEditDraft({
      site: (r.site === "---" || r.site === "—") ? "" : (r.site || ""),
      email: r.email || "",
      password: r.password || "",
    });
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft({ site: "", email: "", password: "" });
  };
  const saveEdit = async (r) => {
    try {
      await updateAdvicePasswordMe(r.id, {
        site: editDraft.site,
        email: editDraft.email,
        password: editDraft.password,
      });
      // Optimistic update — reflect changes immediately without a refetch.
      // Refetching immediately after PUT causes a race condition where the DB
      // hasn't committed yet, returning stale data and making the save look like it failed.
      setAdvice((prev) =>
        (prev || []).map((row) =>
          row.id === r.id
            ? { ...row, site: editDraft.site, email: editDraft.email, password: editDraft.password }
            : row
        )
      );
      setNotice({ text: "Saved changes", kind: "success" });
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Save failed";
      setNotice({ text: msg, kind: "danger" });
    } finally {
      cancelEdit();
    }
  };

  const createRow = async () => {
    if (!newRow.site || !newRow.email || !newRow.password) {
      setNotice({ text: "Site, email and password are required", kind: "danger" });
      return;
    }
    try {
      await createAdvicePasswordMe({
        site: newRow.site || "",
        email: newRow.email,
        password: newRow.password,
        main: false,
      });
      setNotice({ text: "Added new email/password", kind: "success" });
      const updated = await fetchAdvicePasswordsMe().catch(() => advice);
      setAdvice(updated || advice);
      setNewRow({ site: "", email: "", password: "" });
      setAdding(false);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Create failed";
      setNotice({ text: msg, kind: "danger" });
    }
  };

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const getButtonState = useCallback(
    (port) => {
      const state = getActivationButtonState(port, portStatus);
      if (state.text === 'Activating…') {
        return {
          ...state,
          text: (
            <>
              <span className="spinner-border spinner-border-sm me-1" />
              Activating…
            </>
          ),
        };
      }
      return state;
    },
    [portStatus]
  );

  const getStatusBadge = useCallback(
    (port) => {
      const portIdNum = port?.id != null ? Number(port.id) : null;
      const countdown =
        portIdNum != null
          ? portCountdowns[portIdNum] ?? portCountdowns[port.id]
          : null;
      return (
        <PortActivationBadge
          statusEntry={portStatus[port?.id] || {}}
          countdown={countdown}
        />
      );
    },
    [portStatus, portCountdowns]
  );

  if (loading) {
    return (
      <div className="container-fluid mt-4">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2 text-muted">Loading your vault...</p>
        </div>
      </div>
    );
  }

  if (err) {
    return (
      <div className="container-fluid mt-4">
        <div className="alert alert-danger">{err}</div>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-3">
      {/* Smart polling runners for post-activation status checks */}
      {Array.from(pollingPorts.entries()).map(([portId, { gatewayId: gid, portNumber: pnum }]) => (
        <PollingRunner
          key={portId}
          portId={portId}
          gatewayId={gid}
          portNumber={pnum}
          setPortStatusFromActivity={setPortStatusFromActivity}
          stopActivationPolling={stopActivationPolling}
          refreshActivation={refreshActivation}
        />
      ))}
      <style>{`
        @keyframes badge-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }
        .badge.bg-warning .spinner-border { animation: badge-pulse 1.5s ease-in-out infinite; }
        .card { border-radius: 6px; }
        .card-body { padding: 0.75rem; }
        .table-sm td, .table-sm th { padding: 0.4rem; vertical-align: middle; }
        .btn { transition: all 0.2s ease; }
        .badge { font-size: 0.8rem; padding: 0.35em 0.65em; }
      `}</style>

      {/* Header */}
      <div className="row g-2 align-items-center mb-3">
        <div className="col-12 col-md-3 order-1 order-md-1">
          <h4 className="mb-0">Welcome{userCode ? `, ${userCode}` : ""}</h4>
        </div>

        <div className="col-12 col-md-3 order-2 order-md-3 d-flex justify-content-md-end align-items-center gap-2 flex-wrap">
          {/* Hardware sync — prominent orange button always visible in header */}
          <button
            onClick={handleSyncFromHardware}
            disabled={syncing}
            title={`Pull live state from the SIM box and fix stale statuses${lastSyncTime ? ` · Last synced ${lastSyncTime.toLocaleTimeString()}` : ''}`}
            style={{
              fontWeight: 700,
              fontSize: '13px',
              padding: '0.35rem 0.85rem',
              borderRadius: '6px',
              border: '2px solid #e65c00',
              background: syncing ? '#cc7a00' : 'linear-gradient(135deg,#ff8c00,#e65c00)',
              color: '#fff',
              boxShadow: '0 2px 6px rgba(230,92,0,0.35)',
              cursor: syncing ? 'not-allowed' : 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            {syncing ? (
              <><span className="spinner-border spinner-border-sm me-1" />Syncing…</>
            ) : (
              '⚡ Sync from Hardware'
            )}
          </button>
          <button className="btn btn-danger btn-sm" onClick={() => setShowReportProblem(true)}>
            🚨 Report Problem
          </button>
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={() => setShowForwardingApproval(true)}
          >
            📧 Request Forwarding Approval
          </button>
          <button className="btn btn-outline-secondary btn-sm" onClick={signOut}>
            Sign Out
          </button>
        </div>

        {/* Center: search */}
        <div className="col-12 col-md-6 order-3 order-md-2 d-flex justify-content-center">
          <input
            className="form-control form-control-sm text-center"
            placeholder="Search..."
            style={{ maxWidth: 560 }}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Global notice */}
      {notice && (
        <InlineNotice
          message={notice.text}
          kind={notice.kind}
          duration={5000}
          onClose={() => setNotice(null)}
        />
      )}

      {/* Activity error */}
      {activityError && (
        <div className="alert alert-warning alert-dismissible fade show py-2" role="alert">
          <small>Status sync issue: {activityError}</small>
          <button type="button" className="btn-close" onClick={() => setActivityError("")}></button>
        </div>
      )}

      {/* Add new row */}
      <div className="card mb-3 shadow-sm">
        <div className="card-body">
          {!adding ? (
            <div className="d-flex flex-wrap gap-2 align-items-center">
              <button className="btn btn-sm btn-primary" onClick={() => setAdding(true)}>
                + Add Email/Password
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-warning"
                onClick={openBulkConversion}
                disabled={eligibleConversionPorts.length === 0}
                title={
                  eligibleConversionPorts.length === 0
                    ? "No gateway numbers are eligible for conversion"
                    : "Request TT OTP conversion for multiple numbers"
                }
                style={{ fontWeight: 700, borderWidth: 2 }}
              >
                Bulk convert to TT OTP
                {eligibleConversionPorts.length > 0
                  ? ` (${eligibleConversionPorts.length})`
                  : ""}
              </button>
            </div>
          ) : (
            <div className="d-flex justify-content-end">
              <div className="row g-2 align-items-end" style={{ maxWidth: 760 }}>
                <div className="col-12 col-md-3">
                  <SearchableSelect
                    value={newRow.site}
                    onChange={(value) => setNewRow({ ...newRow, site: value })}
                    options={sites.map(site => ({ value: site, label: site }))}
                    placeholder="Site"
                    allowCustom={false}
                    onRequestSite={async (siteName) => {
                      const result = await submitSiteRequest(siteName);
                      if (result.ok) {
                        setNotice({ text: `Request submitted for "${siteName}"`, kind: "success" });
                      } else {
                        setNotice({ text: result.detail || "Failed to submit request", kind: "danger" });
                      }
                    }}
                  />
                </div>
                <div className="col-12 col-md-3">
                  <input
                    className="form-control form-control-sm"
                    placeholder="Email"
                    value={newRow.email}
                    onChange={(e) => setNewRow({ ...newRow, email: e.target.value })}
                  />
                </div>
                <div className="col-12 col-md-3">
                  <input
                    className="form-control form-control-sm"
                    placeholder="Password"
                    type="password"
                    value={newRow.password}
                    onChange={(e) => setNewRow({ ...newRow, password: e.target.value })}
                  />
                </div>
                <div className="col-12 col-md-3">
                  <div className="d-flex gap-2 justify-content-md-end">
                    <button className="btn btn-sm btn-success" onClick={createRow}>
                      Save
                    </button>
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => {
                        setAdding(false);
                        setNewRow({ site: "", email: "", password: "" });
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* SIM Box Status - lets users force-activate stuck numbers */}
      <UserPortStatusPanel
        ports={ports}
        portStatus={portStatus}
        portCountdowns={portCountdowns}
        onActivatePort={handleActivatePort}
        onRefreshActivity={refreshActivation}
        onRequestSimConversion={handleRequestSimConversion}
      />

      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <ConversionStatusFilter
          value={columnFilters.conversionStatus || ""}
          onChange={(conversionStatus) =>
            setColumnFilters({ ...columnFilters, conversionStatus })
          }
          showNewTtEmail
        />
      </div>

      {/* Advice Passwords Table */}
      <AdvicePasswordsTable
        filteredAdvice={filteredAdvice}
        groupedByUserCode={groupAdviceByUserCode(filteredAdvice, userCode)}
        portsByEmail={portsByEmail}
        editingId={editingId}
        editDraft={editDraft}
        startEdit={startEdit}
        cancelEdit={cancelEdit}
        saveEdit={saveEdit}
        setEditDraft={setEditDraft}
        toggleSort={toggleSort}
        sortField={sortField}
        sortDir={sortDir}
        onActivatePort={handleActivatePort}
        onReportProblem={handleReportProblem}
        getButtonState={getButtonState}
        getStatusBadge={getStatusBadge}
        setDetailsRecord={setDetailsRecord}
        setShowDetails={setShowDetails}
        portStatus={portStatus}
        portMessages={portMessages}
        portCountdowns={portCountdowns}
        clearPortMessage={clearPortMessage}
        /** 🔑 These two props make the column search inputs work */
        columnFilters={columnFilters}
        setColumnFilters={setColumnFilters}
        sites={sites}
        onUpdatePort={handleUpdatePort}
        onClearPort={handleClearPort}
        onDeleteAdvicePassword={handleDeleteAdvicePassword}
        isStaff={false}
        duplicateEmails={duplicateEmails}
        duplicateNumbers={duplicateNumbers}
        onRequestForwardingApproval={handleRowForwardingApproval}
        availablePorts={ports.filter((p) => !p.archived && p.gateway?.owner_user_code)}
        allPorts={displayPorts}
        onAssignPort={handleAssignPort}
        onPersonaCheckSubmit={handlePersonaCheckSubmit}
        onPersonaCheckClear={handlePersonaCheckClear}
        onSelfTransferToggle={handleSelfTransferToggle}
        onRequestSimConversion={handleRequestSimConversion}
        pendingTmRequestKeys={pendingTmKeys}
        tmRequestsByKey={tmRequestsByKey}
        completedTmByOriginalKey={completedTmOriginal}
        completedTmByNewKey={completedTmNew}
        onTmAccountRequested={(record, created) => {
          const key = tmRequestRowKey(created || record);
          if (!key) return;
          setPendingTmKeys((prev) => {
            const next = new Set(prev);
            next.add(key);
            return next;
          });
          if (created) {
            setTmRequestsByKey((prev) => {
              const next = new Map(prev);
              next.set(key, created);
              return next;
            });
          }
        }}
      />

      {/* Unmatched Ports Table */}
      {unmatchedPorts.length > 0 && (
        <UnmatchedPortsTable
          unmatchedPorts={unmatchedPorts}
          portsByEmail={portsByEmail}
          advice={advice}
          onActivatePort={handleActivatePort}
          getButtonState={getButtonState}
          getStatusBadge={getStatusBadge}
          portStatus={portStatus}
          portMessages={activationPortMessages}
          portCountdowns={portCountdowns}
          clearPortMessage={clearPortMessage}
          onPortUpdate={async () => {
            // Refresh ports
            const [pr, gatewayPorts] = await Promise.all([
              fetchPortsMe().catch(() => []),
              getMyGatewayPorts().catch(() => []),
            ]);
            const regularPorts = pr || [];
            const gatewayPortsList = gatewayPorts || [];
            const portMap = new Map();
            regularPorts.forEach(port => {
              if (port?.id) portMap.set(port.id, port);
            });
            gatewayPortsList.forEach(port => {
              if (!port?.id) return;
              const existing = portMap.get(port.id);
              const hasNewEmail = (port.user_email || '').trim().length > 0;
              const hasExistingEmail = (existing?.user_email || '').trim().length > 0;
              if (!existing || (hasNewEmail && !hasExistingEmail)) {
                portMap.set(port.id, port);
              }
            });
            setPorts(Array.from(portMap.values()));
          }}
          onAdviceUpdate={async () => {
            // Refresh advice passwords
            const updated = await fetchAdvicePasswordsMe().catch(() => advice);
            setAdvice(updated || advice);
          }}
          duplicateEmails={duplicateEmails}
          duplicateNumbers={duplicateNumbers}
        />
      )}

      {/* Details modal */}
      <DetailsModal
        open={showDetails}
        onClose={() => setShowDetails(false)}
        record={detailsRecord}
        tmRequest={detailsRecord?.tmAccountRequest || null}
        isStaff={false}
        profile={profile}
      />

      {/* Report Problem Modal */}
      <ReportProblemModal
        open={showReportProblem}
        onClose={() => {
          setShowReportProblem(false);
          setReportProblemPort(null);
        }}
        port={reportProblemPort}
      />

      <ForwardingApprovalModal
        open={showForwardingApproval}
        onClose={() => {
          setShowForwardingApproval(false);
          setForwardingPrefillEmail("");
        }}
        emails={advice.map((r) => r.email).filter(Boolean)}
        prefillEmail={forwardingPrefillEmail}
      />

      <ConvertToTtOtpModal
        open={bulkConversionOpen}
        bulk
        ports={eligibleConversionPorts}
        isStaff={false}
        onClose={() => setBulkConversionOpen(false)}
        onConfirm={handleBulkRequestSimConversion}
      />
    </div>
  );
}
